/**
 * Secure Storage Utility
 * 
 * Provides safe localStorage operations with error handling and validation
 */

/**
 * Safely set a value in localStorage
 * @param {string} key - The key to store the value under
 * @param {*} value - The value to store (will be JSON stringified)
 * @returns {boolean} - Returns true if successful, false otherwise
 */
export const setSecureStorage = (key, value) => {
  try {
    // Validate key
    if (!key || typeof key !== 'string') {
      console.error('Invalid storage key:', key);
      return false;
    }

    // Stringify value if it's not already a string
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    
    localStorage.setItem(key, stringValue);
    return true;
  } catch (error) {
    // Handle quota exceeded or localStorage disabled
    if (error.name === 'QuotaExceededError') {
      console.error('localStorage quota exceeded. Cannot store:', key);
    } else if (error.name === 'SecurityError') {
      console.error('localStorage access denied (SecurityError):', error);
    } else {
      console.error('Failed to set localStorage:', error);
    }
    return false;
  }
};

/**
 * Safely get a value from localStorage
 * @param {string} key - The key to retrieve
 * @param {*} defaultValue - Default value if key doesn't exist or parsing fails
 * @returns {*} - The parsed value or defaultValue
 */
export const getSecureStorage = (key, defaultValue = null) => {
  try {
    if (!key || typeof key !== 'string') {
      console.error('Invalid storage key:', key);
      return defaultValue;
    }

    const item = localStorage.getItem(key);
    
    if (item === null) {
      return defaultValue;
    }

    // Try to parse as JSON, fallback to string
    try {
      return JSON.parse(item);
    } catch {
      // If parsing fails, return as string
      return item;
    }
  } catch (error) {
    if (error.name === 'SecurityError') {
      console.error('localStorage access denied (SecurityError):', error);
    } else {
      console.error('Failed to get localStorage:', error);
    }
    return defaultValue;
  }
};

/**
 * Safely remove a value from localStorage
 * @param {string} key - The key to remove
 * @returns {boolean} - Returns true if successful, false otherwise
 */
export const removeSecureStorage = (key) => {
  try {
    if (!key || typeof key !== 'string') {
      console.error('Invalid storage key:', key);
      return false;
    }

    localStorage.removeItem(key);
    return true;
  } catch (error) {
    if (error.name === 'SecurityError') {
      console.error('localStorage access denied (SecurityError):', error);
    } else {
      console.error('Failed to remove localStorage:', error);
    }
    return false;
  }
};

/**
 * Check if localStorage is available
 * @returns {boolean} - Returns true if localStorage is available
 */
export const isStorageAvailable = () => {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

